import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import st from 'core/shared-translations';
import TextInput from 'components/text-input-view';
import EmailInputGroup from 'components/email-input-group-view';
import CloseButton from 'components/close-button-view';
import StaticLink from 'components/static-link-view';
import EmailVerificationContainer from '../containers/email-verification-container';
import {
  FormattedMessage,
  defineMessages,
  useIntl,
} from 'react-intl';
import classes from './signin-security.scss';
import { getModalParent } from 'lib/utility-functions';

const t = defineMessages({
  'email-editor.email-address': {
    id: 'email-editor.email-address',
    defaultMessage: 'Email Address',
    description: 'Title string for the email editor section',
  },
  'email-editor.primary-email': {
    id: 'email-editor.primary-email',
    defaultMessage: 'Primary email',
    description: 'Field label for Primary email',
  },
  'email-editor.select-domain': {
    id: 'email-editor.select-domain',
    defaultMessage: 'Select a domain',
    description: 'Title attribute for the domain dropdown list',
  },
  'email-editor.recovery-email': {
    id: 'email-editor.recovery-email',
    defaultMessage: 'Recovery email',
    description: 'Field label for Recovery email',
  },
  'email-editor.edit-email-addresses': {
    id: 'email-editor.edit-email-addresses',
    defaultMessage: 'Edit Email Addresses',
    description: 'Title string for the email editor modal dialog',
  },
  'email-editor.primary-email-info': {
    id: 'email-editor.primary-email-info',
    defaultMessage: 'You\'ll sign in to your account with this email address.',
    description: 'Information string to appear below the Primary Email form field',
  },
  'email-editor.recovery-email-info': {
    id: 'email-editor.recovery-email-info',
    defaultMessage: 'We\'ll use this to help you reset your password if you forget it.',
    description: 'Information string to appear below the Recovery Email form field',
  },
  'email-editor.validation-error-email': {
    id: 'email-editor.validation-error-email',
    defaultMessage: 'Please enter a valid email address',
    description: 'Content of the error message when the email address field is left blank or contains invalid characters',
  },
  'email-editor.validation-error-domain-entered': {
    id: 'email-editor.validation-error-domain-entered',
    defaultMessage: 'Enter the unique portion of your email address and select your domain from the list.',
    description: 'Content of the error message when the user is part of an organization and he/she enters the full email address without selecting a domain from the dropdown list',
  },
  'email-editor.validation-error-email-length-exceeded': {
    id: 'email-editor.validation-error-email-length-exceeded',
    defaultMessage: 'The email address must not exceed 128 characters',
    description: 'Content of the error message when the email address length exceeds 128 characters',
  },
  'email-editor.saml-sso-email-info': {
    id: 'email-editor.saml-sso-email-info',
    defaultMessage: 'You\'ll sign into your account with this email address.',
    description: 'Info string to appear below the email filed when the email address is under SAML SSO org policy',
  },
});

export const EmailEditor = ({
  personData,
  isEmailChangeAllowed,
  onEditLinkClick,
  modalIsOpen,
  closeModal,
  completeProcess,
  onBlur,
  onChange,
  onSave,
  enableSubmit,
  isLoading,
  errors,
  domainValue,
  domainUserName,
  recoveryEmail,
  domainOptions,
  onDomainChange,
  showEmailEditor,
  modifiedUserName,
  handlePrimaryEmailVerification,
  emailEditorClass,
  verifyEmailClass,
  forceSamlSsoLogin,
  forceSamlSsoClass,
  isViewOnly = false,
}) => {
  const intl = useIntl();
  return (
    <div>
      <div className="row">
        <div id="email-address-section-title" className="col-xs-9 sectionTitle">
          <FormattedMessage {...t['email-editor.email-address']} />
        </div>
        {isEmailChangeAllowed &&
          <div className={`col-xs-3 no-padding ${classes[isViewOnly ? 'editLinkViewOnly': 'editLink']}`}>
            {isViewOnly &&
              <StaticLink
                value={intl.formatMessage(st['shared.edit'])}
                showEditIcon
              />}
            {!isViewOnly &&
              <a
                id="edit-email-link"
                href="#"
                aria-label={intl.formatMessage(t['email-editor.edit-email-addresses'])}
                title={intl.formatMessage(t['email-editor.edit-email-addresses'])}
                onClick={onEditLinkClick}>
                <i className="togo-icon togo-icon-edit" />
                &nbsp;
                <FormattedMessage {...st['shared.edit']} />
              </a>}
          </div>}
      </div>
      <div id="primary-email-label" className={classes['staticFieldLabel']}>
        <FormattedMessage {...t['email-editor.primary-email']} />
      </div>
      <div id="primary-email-value" className={classes['staticFieldValue']}>
        {personData.userName ||
          <FormattedMessage {...st['shared.none-set']} />}
      </div>
      <div id="recovery-email-label" className={`${classes['staticFieldLabel']} ${forceSamlSsoClass}`}>
        <FormattedMessage {...t['email-editor.recovery-email']} />
      </div>
      <div id="recovery-email-value" className={`${classes['staticFieldValue']} ${forceSamlSsoClass}`}>
        {recoveryEmail ||
          <FormattedMessage {...st['shared.none-set']} />}
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="EditEmailModal"
        shouldCloseOnOverlayClick={false}
        className={classes['modal']}
        overlayClassName={classes['overlay']}
        parentSelector={getModalParent}
      >
        <CloseButton
          ariaLabel={intl.formatMessage(st['shared.close'])}
          onClick={closeModal}
        />
        <div id="EmailEditor" className={emailEditorClass}>
          <div id="email-editor-title" className="modalTitle">
            <FormattedMessage {...t['email-editor.edit-email-addresses']} />
          </div>
          <div className="modalContent">
            <div className="contentDivPadding">
              {!domainOptions.length &&
                <TextInput
                  name="userName"
                  label={intl.formatMessage(t['email-editor.primary-email'])}
                  helpText=""
                  value={personData.userName}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={
                    errors.userName &&
                    intl.formatMessage(st[errors.userName] || t[errors.userName])
                  }
                  infoText={intl.formatMessage(t[forceSamlSsoLogin ? 'email-editor.saml-sso-email-info' : 'email-editor.primary-email-info'])}
                />}
              {!!domainOptions.length &&
                <EmailInputGroup
                  name="domainUserName"
                  label={intl.formatMessage(t['email-editor.primary-email'])}
                  helpText=""
                  value={domainUserName}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={
                    errors.domainUserName &&
                    intl.formatMessage(st[errors.domainUserName] || t[errors.domainUserName])
                  }
                  infoText={intl.formatMessage(t[forceSamlSsoLogin ? 'email-editor.saml-sso-email-info' : 'email-editor.primary-email-info'])}
                  domainValue={domainValue}
                  domainOptions={domainOptions}
                  onDomainChange={onDomainChange}
                  domainSelectLabel={intl.formatMessage(t['email-editor.select-domain'])}
                />}
              {!forceSamlSsoLogin &&
                <TextInput
                  name="recoveryEmail"
                  label={intl.formatMessage(t['email-editor.recovery-email'])}
                  helpText=""
                  value={recoveryEmail}
                  onChange={onChange}
                  onBlur={onBlur}
                  infoText={intl.formatMessage(t['email-editor.recovery-email-info'])}
                  error={
                    errors.recoveryEmail &&
                    intl.formatMessage(st[errors.recoveryEmail] || t[errors.recoveryEmail])
                  }
                />}
            </div>
            <div className="row buttonRow">
              <div className="col-xs-12 col-sm-6 no-gutter">
                <button
                  id="email-editor-save-button"
                  type="button"
                  disabled={!enableSubmit}
                  className="btn btn-default ninety-button marginl-5"
                  onClick={onSave}>
                  {isLoading ?
                    <FormattedMessage {...st['shared.saving']} /> :
                    <FormattedMessage {...st['shared.save']} />}
                </button>
              </div>
              <div className="col-xs-12 col-sm-6 no-gutter">
                <button
                  id="email-editor-cancel-button"
                  type="button"
                  className="btn btn-tertiary ninety-button"
                  onClick={closeModal}>
                  <FormattedMessage {...st['shared.cancel']} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div id="VerifyEmail" className={verifyEmailClass}>
          <EmailVerificationContainer
            personData={personData}
            showEmailEditor={showEmailEditor}
            userName={modifiedUserName}
            handlePrimaryEmailVerification={handlePrimaryEmailVerification}
            closeModal={closeModal}
            completeProcess={completeProcess}
          />
        </div>
      </Modal>
    </div>
  );
};

EmailEditor.propTypes = {
  personData: PropTypes.object.isRequired,
  isEmailChangeAllowed: PropTypes.bool.isRequired,
  onEditLinkClick: PropTypes.func.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  completeProcess: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  enableSubmit: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  domainUserName: PropTypes.string.isRequired,
  domainValue: PropTypes.string.isRequired,
  domainOptions: PropTypes.array.isRequired,
  onDomainChange: PropTypes.func.isRequired,
  recoveryEmail: PropTypes.string.isRequired,
  showEmailEditor: PropTypes.func.isRequired,
  modifiedUserName: PropTypes.string,
  handlePrimaryEmailVerification: PropTypes.func.isRequired,
  emailEditorClass: PropTypes.string.isRequired,
  verifyEmailClass: PropTypes.string.isRequired,
  forceSamlSsoLogin: PropTypes.bool.isRequired,
  forceSamlSsoClass: PropTypes.string.isRequired,
  isViewOnly: PropTypes.bool,
};

export default EmailEditor;
